import React from 'react'

import {
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot,
  DragDropContext,
} from 'react-beautiful-dnd'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'

import Table from 'components/layout/table'
import TableData from './tableData'
import { ISelectTableHead } from './head'
import { ISelectItem, ISelectTableConfig } from 'components/layout/selectTable'
import { DragIndicator } from 'images/icons'
import * as config from 'styles/tailwind.config'
import CheckBox from 'components/ui/checkBox'

interface IProps {
  config: ISelectTableConfig
  data: ISelectItem[]
  isDragDisabled?: boolean
  onOrderChange?: (
    newItems: ISelectItem[],
    source: number,
    destination: number,
    item: ISelectItem,
  ) => void
  onSelectedChange?: (selected: { [k: string]: string }) => void
}

interface IState {
  items: ISelectItem[]
  selected: {
    [k: string]: string
  }
}

class SelectTableBody extends Table<IProps, IState> {
  constructor(props: any) {
    super(props)

    this.state = {
      items: props.data,
      selected: {},
    }
  }

  componentWillReceiveProps(nextProps: any) {
    const { data: nextItems } = nextProps

    if (!isEqual(nextItems, this.state.items)) {
      this.setState({ items: nextItems })
    }
  }

  reorder = (items: ISelectItem[], sourceIndex: number, destinationIndex: number) => {
    const result = Array.from(items)

    // Remove the item from the source index
    const [removed] = result.splice(sourceIndex, 1)

    // Insert the item at the destination index
    result.splice(destinationIndex, 0, removed)

    return result
  }

  onDragEnd = (result: DropResult) => {
    const { items } = this.state
    const { onOrderChange } = this.props
    const { destination, source } = result

    // Drag has no destination, no reordering necessary
    if (!destination) {
      return
    }

    // Check if the destination and source are the same
    // if so no reordering is necessary
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return
    }

    const newItems = this.reorder(items, source.index, destination.index)

    this.setState({ items: newItems })

    if (onOrderChange) {
      onOrderChange(newItems, source.index, destination.index, newItems[destination.index])
    }
  }

  handleSelect = (isSelected: boolean, id: string) => {
    const selectedNew = this.state.selected
    const { onSelectedChange } = this.props

    const cb = (selected: { [k: string]: string }) => {
      if (onSelectedChange) {
        onSelectedChange(selected)
      }
    }

    if (isSelected) {
      this.setState(
        prevState => ({
          selected: {
            ...prevState.selected,
            [id]: id,
          },
        }),
        () => cb(this.state.selected),
      )
    } else {
      delete selectedNew[id]
      this.setState(
        {
          selected: selectedNew,
        },
        () => cb(this.state.selected),
      )
    }
  }

  toggleAll = (selectAll: boolean) => {
    const { items } = this.state
    const { onSelectedChange } = this.props

    const cb = (selected: { [k: string]: string }) => {
      if (onSelectedChange) {
        onSelectedChange(selected)
      }
    }

    if (selectAll) {
      const selectedAll = items.reduce((obj: { [k: string]: string }, item: ISelectItem) => {
        obj[item.id] = item.id
        return obj
      }, {})

      this.setState(
        {
          selected: selectedAll,
        },
        () => cb(this.state.selected),
      )
    } else {
      this.setState(
        {
          selected: {},
        },
        () => cb(this.state.selected),
      )
    }
  }

  public render() {
    const { items, selected } = this.state
    const { isDragDisabled } = this.props
    const { head, dataSetName } = this.props.config

    const getItemStyle = (draggableStyle: any, isDragging: boolean, isSelected: boolean) => ({
      ...draggableStyle,
      userSelect: 'none',
      backgroundColor: isDragging || isSelected ? config.theme.colors['gray-100'] : 'unset',
      boxShadow: isDragging ? config.theme.boxShadow['shadow-md'] : 'unset',
    })

    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <Droppable droppableId="test">
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {items && items.length ? (
                items.map((item: ISelectItem, index) => (
                  <Draggable
                    key={String(get(item, 'id'))}
                    draggableId={String(get(item, 'id'))}
                    index={index}
                    isDragDisabled={isDragDisabled}
                  >
                    {(
                      providedDraggable: DraggableProvided,
                      snapshotDraggable: DraggableStateSnapshot,
                    ) => (
                      <div>
                        <div
                          ref={providedDraggable.innerRef}
                          {...providedDraggable.draggableProps}
                          {...providedDraggable.dragHandleProps}
                          style={getItemStyle(
                            providedDraggable.draggableProps.style,
                            snapshotDraggable.isDragging,
                            selected.hasOwnProperty(item.id),
                          )}
                          className="flex"
                        >
                          <div
                            className={`w-full relative flex items-center mx-48 py-10 border-b-1 ${
                              snapshotDraggable.isDragging ? 'border-gray-100' : 'border-gray-200'
                            }`}
                          >
                            {!isDragDisabled && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  left: '-36px',
                                }}
                              >
                                <DragIndicator light />
                              </div>
                            )}
                            <div className="mr-10">
                              <CheckBox
                                checked={selected[item.id] ? true : false}
                                onChange={value => this.handleSelect(value, item.id)}
                                value={item.id}
                              />
                            </div>
                            {head.map((headItem: ISelectTableHead) => {
                              return (
                                <TableData
                                  id={item.id}
                                  key={headItem.key}
                                  name={headItem.key}
                                  col={headItem.col}
                                  text={this.getValue(item, headItem.key)}
                                  leftAlign={headItem.leftAlign}
                                  unit={
                                    this.getUnit(item, headItem.key) || this.getHeadUnit(headItem)
                                  }
                                  linkTo={headItem.linkTo ? headItem.linkTo : undefined}
                                />
                              )
                            })}
                          </div>
                        </div>
                        {providedDraggable.placeholder}
                      </div>
                    )}
                  </Draggable>
                ))
              ) : (
                <p className={`w-full px-48 py-10 text-black text-13 font-400 tracking-0.1`}>
                  Es sind keine {dataSetName} vorhanden.
                </p>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    )
  }
}

export default SelectTableBody
